import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Protect Yourself and Others".
 *
 * An account settings screen that behaves like a real one. Nine
 * changes, each with an honest effect - and three of them are security
 * theatre, which is the part worth building: changing your password
 * every ninety days, a security question anybody can look up, and
 * text-message codes that feel like protection and are the weakest of
 * the three second steps.
 *
 * The meter moves on what actually helps. A learner who does everything
 * scores well; a learner who does the three noisy ones and nothing else
 * barely moves, and the review at the end says why.
 *
 * Last comes the part the lesson is named for: the same account, but
 * somebody else's, and what you can do for people who will never read
 * any of this.
 */

const STORE_KEY = 'cybercub.lab.harden';

/*
 * value: what this is genuinely worth, 0-20.
 * theatre: feels like security, is not. Named on the review.
 */
const ACTIONS = [
  {
    id: 'passphrase',
    group: 'Signing in',
    icon: '🔑',
    label: 'Replace the password with three random words',
    was: 'Summer2024!',
    now: 'otter-plank-cinnamon',
    value: 14,
    note: 'Longer beats cleverer. The exclamation mark was never fooling anybody.',
  },
  {
    id: 'unique',
    group: 'Signing in',
    icon: '🗂️',
    label: 'Stop reusing it — a different password for every site',
    was: 'the same one on 14 sites',
    now: 'all different, in a password manager',
    value: 18,
    note: 'This is the one that matters most. One site leaking should cost you one account, not fourteen.',
  },
  {
    id: 'rotate',
    group: 'Signing in',
    icon: '🔁',
    label: 'Set the password to expire every 90 days',
    was: 'never expires',
    now: 'expires every 90 days',
    value: 0,
    theatre: true,
    note: 'Forced changes make people pick worse passwords, in predictable patterns. Every serious guideline dropped this years ago.',
  },
  {
    id: 'app2fa',
    group: 'Second step',
    icon: '📱',
    label: 'Turn on two-step sign-in with an app',
    was: 'off',
    now: 'authenticator app',
    value: 20,
    note: 'A stolen password stops being enough. This is the single biggest change on this screen.',
  },
  {
    id: 'sms2fa',
    group: 'Second step',
    icon: '💬',
    label: 'Use text-message codes instead',
    was: 'off',
    now: 'codes by SMS',
    value: 9,
    theatre: true,
    excludes: 'app2fa',
    note: 'Far better than nothing, and the weakest of the options — a phone number can be moved to somebody else’s SIM by talking to a shop. Use the app if you can.',
  },
  {
    id: 'codes',
    group: 'Second step',
    icon: '🎟️',
    label: 'Save the backup codes somewhere safe',
    was: 'never generated',
    now: 'printed and in a drawer',
    value: 8,
    note: 'The reason people turn two-step off again is losing their phone. These are what stop that.',
  },
  {
    id: 'recovery',
    group: 'Getting back in',
    icon: '📧',
    label: 'Check the recovery email is still yours',
    was: 'an old school address you cannot open',
    now: 'your current address',
    value: 12,
    note: 'Recovery is a second front door. An address you no longer control is a key somebody else may hold.',
  },
  {
    id: 'question',
    group: 'Getting back in',
    icon: '❓',
    label: 'Set a security question: mother’s maiden name',
    was: 'not set',
    now: 'answered truthfully',
    value: 0,
    theatre: true,
    note: 'A true answer to a public question is a password anybody can research. If a site forces one, answer it with nonsense and store the nonsense.',
  },
  {
    id: 'sessions',
    group: 'Housekeeping',
    icon: '🖥️',
    label: 'Sign out the devices you do not recognise',
    was: '6 devices, 2 unknown',
    now: '4 devices, all yours',
    value: 10,
    note: 'Changing a password does not always end sessions already signed in. This does.',
  },
  {
    id: 'apps',
    group: 'Housekeeping',
    icon: '🧩',
    label: 'Remove the connected apps you have stopped using',
    was: '9 apps, 5 unused since 2023',
    now: '4 apps',
    value: 9,
    note: 'Each one still holds permission to read this account. A quiz app from three years ago is a door you forgot you opened.',
  },
];

const GROUPS = ['Signing in', 'Second step', 'Getting back in', 'Housekeeping'];

const MAX = ACTIONS.filter((a) => a.id !== 'sms2fa').reduce((n, a) => n + a.value, 0);

/* ------------------------------------------------------------------ */
/* looking after other people                                         */
/* ------------------------------------------------------------------ */

const OTHERS = [
  {
    id: 'o1',
    icon: '👵',
    text: 'Your grandmother rings: a man from "the bank" is on the other line asking her to read out a code she has just been texted.',
    options: [
      'Tell her to read it out — the bank sent it, so it must be them',
      'Tell her to hang up now, and that nobody legitimate ever asks for that code',
      'Tell her to ring you back later',
    ],
    answer: 1,
    why: 'That code is the second step of somebody else signing in as her, right now. The single rule worth teaching anybody: a code that arrives on your phone is never read out to a person.',
  },
  {
    id: 'o2',
    icon: '👦',
    text: 'Your younger brother’s game account was stolen. He is embarrassed and has not told anyone.',
    options: [
      'Tell him he should have known better',
      'Help him recover it, and make sure he knows telling somebody fast is the thing that limits the damage',
      'Ignore it — it is only a game',
    ],
    answer: 1,
    why: 'Shame is what attackers rely on. A person who is frightened of being blamed waits, and waiting is what turns a stolen game account into a stolen everything.',
  },
  {
    id: 'o3',
    icon: '🏫',
    text: 'You find a way into your school’s system that lets you see other pupils’ files.',
    options: [
      'Show your friends so they believe you',
      'Tell a teacher or the IT staff privately, and do not look at anything further',
      'Post about it so the school has to fix it',
    ],
    answer: 1,
    why: 'Exactly the responsible disclosure from the lesson, applied to somewhere you actually are. Telling quietly gives them a chance to fix it; telling loudly hands it to everybody else first.',
  },
  {
    id: 'o4',
    icon: '💬',
    text: 'A friend forwards you a message: "free gift cards, just sign in with your account here to claim".',
    options: [
      'Try it yourself first to see if it works',
      'Tell them it is a trap and that whoever sent it to them has probably already been caught by it',
      'Say nothing — they will work it out',
    ],
    answer: 1,
    why: 'These spread because each victim becomes the next sender. Telling them is not being rude; it is the only thing that stops the chain.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function HardenLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('account');
  const [done, setDone] = useState(saved.done ?? []);
  const [reviewed, setReviewed] = useState(saved.reviewed ?? false);
  const [picks, setPicks] = useState(saved.picks ?? {});

  useEffect(() => { document.title = 'Harden the account · CyberCub Lab'; }, []);
  useEffect(() => { save({ done, reviewed, picks }); }, [done, reviewed, picks]);

  const chosen = ACTIONS.filter((a) => done.includes(a.id));
  const score = Math.min(100, Math.round((chosen.reduce((n, a) => n + a.value, 0) / MAX) * 100));
  const theatreOnly = chosen.filter((a) => a.theatre).length;

  const toggle = (a) => {
    setDone((prev) => {
      if (prev.includes(a.id)) return prev.filter((x) => x !== a.id);
      // Two-step is one choice, not two. Picking one replaces the other.
      const without = a.excludes ? prev.filter((x) => x !== a.excludes) : prev;
      const clashing = ACTIONS.find((x) => x.excludes === a.id);
      return [...(clashing ? without.filter((x) => x !== clashing.id) : without), a.id];
    });
  };

  const othersRight = OTHERS.filter((o) => picks[o.id] === o.answer).length;
  const othersDone = othersRight === OTHERS.length;

  useFinished('/lab/harden', othersDone);

  const TABS = [
    { id: 'account', n: 1, label: 'Harden the account', locked: false },
    { id: 'others', n: 2, label: 'And everybody else', locked: !reviewed },
  ];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Harden the account</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/eh1l5-protect-yourself-and-others" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the account ---------------- */}
      {tab === 'account' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Security settings</h2>
              <p className="lab-lead">
                A real account screen, with the usual mixture: a few changes that genuinely matter, and
                a few that only look like they do. Make the ones worth making.
              </p>
            </div>
            <span className="pill">{score}% protected</span>
          </div>

          <Tutor
            avatar="cub-mint"
            say={
              reviewed
                ? 'Now the harder half. Everything you just did protects one account belonging to somebody who reads things like this. Most people never will.'
                : score >= 80
                  ? 'That is a genuinely well-defended account. Have a look at the review — one or two of the things on that list did nothing at all, and knowing which is as useful as the rest.'
                  : 'Two on this screen are worth more than everything else put together: not reusing the password anywhere, and a second step from an app. Three others are theatre, and you will find out which.'
            }
          />

          <div className="meter">
            <div className="meter-head">
              <span>How well protected is this account?</span>
              <strong className={score >= 80 ? 'is-good' : ''}>{score}%</strong>
            </div>
            <div className="meter-track">
              <i className={`meter-fill is-protect ${score >= 80 ? 'is-good' : ''}`} style={{ width: `${score}%` }} />
            </div>
          </div>

          {GROUPS.map((g) => (
            <div key={g} className="harden-group">
              <h3>{g}</h3>
              <ul>
                {ACTIONS.filter((a) => a.group === g).map((a) => {
                  const on = done.includes(a.id);
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        className={`harden-row ${on ? 'is-on' : ''}`}
                        onClick={() => toggle(a)}
                        aria-pressed={on}
                      >
                        <span className="harden-icon" aria-hidden="true">{a.icon}</span>
                        <span className="harden-body">
                          <strong>{a.label}</strong>
                          <em>{on ? `Now: ${a.now}` : `Currently: ${a.was}`}</em>
                        </span>
                        <span className={`harden-state ${on ? 'is-on' : ''}`}>{on ? 'Done' : 'Change'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {!reviewed ? (
            <div className="lab-done">
              <div>
                <strong>{done.length} change{done.length === 1 ? '' : 's'} made.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  Finish when you are ready and find out which of them actually did anything.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => { setReviewed(true); }}
                disabled={done.length === 0}
              >
                Review what I changed →
              </button>
            </div>
          ) : (
            <>
              <div className="harden-review">
                <h3>What those changes were worth</h3>
                <ul>
                  {chosen.map((a) => (
                    <li key={a.id} className={a.theatre ? 'is-theatre' : ''}>
                      <span aria-hidden="true">{a.theatre ? '➖' : '✓'}</span>
                      <div>
                        <strong>{a.label}</strong>
                        <p>{a.note}</p>
                      </div>
                    </li>
                  ))}
                  {ACTIONS.filter((a) => !done.includes(a.id) && a.value >= 12).map((a) => (
                    <li key={a.id} className="is-missed">
                      <span aria-hidden="true">✗</span>
                      <div>
                        <strong>Not done: {a.label}</strong>
                        <p>{a.note}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                {theatreOnly > 0 && (
                  <p className="harden-note">
                    {theatreOnly} of your changes were security theatre — real settings, on real
                    sites, that make a screen look after itself without protecting anybody. They are
                    not there to trick you; they are just old advice that never got removed.
                  </p>
                )}
              </div>

              <div className="lab-done">
                <div>
                  <strong>One account is the easy half.</strong>
                  <p style={{ margin: '0.3rem 0 0' }}>
                    The lesson is called protect yourself <em>and others</em>, and the others are the
                    part that needs you.
                  </p>
                </div>
                <button type="button" className="btn btn-sm" onClick={() => setTab('others')}>
                  And everybody else →
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* ---------------- 2. everybody else ---------------- */}
      {tab === 'others' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>And everybody else</h2>
              <p className="lab-lead">
                Four moments with people who will never take a course like this one. What you do in
                each is worth more than anything you changed on your own screen.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setTab('account')}>
                ← Back to the account
              </button>
              <span className="pill">{othersRight} / {OTHERS.length}</span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              othersDone
                ? 'All four, and notice that not one of them needed a single technical skill. The most useful thing anybody with your knowledge does is be the person other people can tell without being laughed at.'
                : 'Ask what each person actually needs from you. Usually it is not information — they need somebody to tell them it is not their fault and help them act quickly.'
            }
          />

          {OTHERS.map((o) => {
            const pick = picks[o.id];
            const correct = pick === o.answer;
            return (
              <div key={o.id} className={`lab-case ${correct ? 'is-right' : pick !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{o.icon}</span>
                <div className="grow">
                  <p>{o.text}</p>
                  <div className="lab-choices">
                    {o.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick === oi ? 'is-picked' : ''}`}
                        onClick={() => setPicks((prev) => ({ ...prev, [o.id]: oi }))}
                        disabled={correct}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick !== undefined && (
                    <p className={`lab-feedback ${correct ? 'is-right' : 'is-wrong'}`}>
                      {correct ? `✓ ${o.why}` : '✗ Not that one — what does this person actually need in the next five minutes?'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {othersDone && (
            <div className="lab-done">
              <div>
                <strong>That is the end of the foundations.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  You know what security is protecting and who it is protecting it for, what makes a
                  hacker ethical, how an attack is assembled long before anything technical happens,
                  and how people are talked into opening the door. The rest of this course is detail
                  on top of those four things.
                </p>
              </div>
              <Link to="/learn/lesson/eh1l5-protect-yourself-and-others" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
